import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createMove } from '../../store/moves';
import { fetchBuildingsUser } from '../../store/buildings';
import { fetchTrailersUser, getAvailableTrailers } from '../../store/trailers';
import { getCookie } from '../../utils/csrf';
import { useHistory } from 'react-router-dom';

const MoveCreationModal = ({ isOpen, onClose }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const [moveDetails, setMoveDetails] = useState({
        type: 'single',
        moves: [{ from: '', to: '', trailer: null }]
    });
    const [selectedBuildings, setSelectedBuildings] = useState([]);
    const [selectedTrailers, setSelectedTrailers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [moveType, setMoveType] = useState('single');
    const [trailerSearch, setTrailerSearch] = useState('');
    const [locationsWithTrailers, setLocationsWithTrailers] = useState([]);
    const [loadingTrailers, setLoadingTrailers] = useState(false);
    const [hasPermission, setHasPermission] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // We'll use separate state objects for each move to ensure independence
    const [moveTrailers, setMoveTrailers] = useState({});  // Available trailers for each move
    const [moveSelections, setMoveSelections] = useState({}); // Selected trailers for each move
    const [loadingMoveIndex, setLoadingMoveIndex] = useState(null); // Track which move is loading trailers

    // Use refs to preserve data between renders
    const moveDataRef = useRef({
        previousMoves: {},
        currentMoveIndex: 0
    });

    // Get current user from session
    const currentUser = useSelector(state => state.session.user);
    const selectedAccountId = useSelector(state => state.ui.selectedAccountId);
    const buildings = useSelector(state => Array.isArray(state.buildings) ? state.buildings : Object.values(state.buildings) || []);
    const allTrailers = useSelector(state => {
        const trailers = state.trailers;
        if (!trailers) return [];
        if (Array.isArray(trailers)) return trailers;
        if (typeof trailers === 'object') {
            // Filter out the "available" key and any non-object values
            return Object.entries(trailers)
                .filter(([key, value]) => key !== 'available' && typeof value === 'object')
                .map(([_, value]) => value);
        }
        return [];
    });

    // Check if user is a manager or above
    const isManagerOrAbove = currentUser && (
        currentUser.is_admin ||
        currentUser.is_manager ||
        currentUser.is_account_manager ||
        currentUser.is_super_manager
    );

    // Check permissions when modal opens
    useEffect(() => {
        if (isOpen) {
            if (!isManagerOrAbove) {
                setHasPermission(false);
                onClose();
                alert("You don't have permission to create moves. Only managers and administrators can create moves.");
            } else {
                setHasPermission(true);
            }
        }
    }, [isOpen, isManagerOrAbove, onClose]);

    // Find all locations that have trailers
    const findLocationsWithTrailers = async () => {
        if (!selectedAccountId) return;

        setLoadingTrailers(true);

        try {
            console.log('Finding locations with trailers...');

            // Use the trailers already loaded in Redux instead of making API calls for each location
            const trailersArray = Array.isArray(allTrailers) ? allTrailers : Object.values(allTrailers);

            // Create a map of locations to count of trailers
            const locationMap = {};

            // Count trailers at each location, including red tagged trailers
            trailersArray.forEach(trailer => {
                if (trailer?.trailer_location) {
                    locationMap[trailer.trailer_location] = (locationMap[trailer.trailer_location] || 0) + 1;
                }
            });

            // Convert to array of locations with trailers
            const locationsWithTrailersFound = Object.keys(locationMap).filter(
                location => locationMap[location] > 0
            );

            console.log('Locations with trailers:', locationsWithTrailersFound);
            setLocationsWithTrailers(locationsWithTrailersFound);
        } catch (err) {
            console.error('Error finding locations with trailers:', err);
        } finally {
            // Use a timeout to prevent rapid state changes
            setTimeout(() => {
                setLoadingTrailers(false);
            }, 100);
        }
    };

    // Load data when modal opens
    useEffect(() => {
        const loadData = async () => {
            if (!selectedAccountId || !isManagerOrAbove) return;

            setIsLoading(true);
            setError(null);

            // Reset to single move type whenever modal is opened
            setMoveType('single');
            setMoveDetails({
                type: 'single',
                moves: [{ from: '', to: '', trailer: null }]
            });

            try {
                // Use the fetchTrailersUser thunk instead of direct fetch
                await dispatch(fetchTrailersUser(selectedAccountId));
                console.log('Refreshed trailers before finding locations');

                // Then load buildings data
                await dispatch(fetchBuildingsUser(selectedAccountId));

                // Find which locations have trailers after getting fresh data
                await findLocationsWithTrailers();
            } catch (err) {
                console.error('Error loading data:', err);
                setError('Failed to load data. Please try again later.');
            } finally {
                setIsLoading(false);
            }
        };

        // Only load data when the modal is actually opened
        if (isOpen && isManagerOrAbove) {
            loadData();
        }
    }, [dispatch, selectedAccountId, isOpen, isManagerOrAbove]);

    // Separate effect to control the loading state - helps break circular dependencies
    useEffect(() => {
        // Only start/stop loading if modal is open
        if (!isOpen) return;

        // Set loading state when data is being loaded
        if (isLoading) {
            setLoadingTrailers(true);
        } else {
            // Delay turning off loading to prevent UI flicker
            const timer = setTimeout(() => {
                setLoadingTrailers(false);
            }, 200);

            return () => clearTimeout(timer);
        }
    }, [isLoading, isOpen]);

    const handleMoveTypeChange = (type) => {
        setMoveType(type);

        // Reset trailer search
        setTrailerSearch('');

        // Update move details based on type
        let newMoves = [];

        switch (type) {
            case 'single':
                newMoves = [{ from: '', to: '', trailer: null }];
                break;
            case 'shuttle':
                newMoves = [
                    { from: '', to: '', trailer: null },
                    { from: '', to: '', trailer: null }
                ];
                break;
            case 'triangle':
                newMoves = [
                    { from: '', to: '', trailer: null },
                    { from: '', to: '', trailer: null },
                    { from: '', to: '', trailer: null }
                ];
                break;
            default:
                newMoves = [{ from: '', to: '', trailer: null }];
                break;
        }

        setMoveDetails({
            type: type,
            moves: newMoves
        });

        // Reset all move-related state
        setSelectedBuildings([]);
        setSelectedTrailers([]);
        setMoveTrailers({});
        setMoveSelections({});
        moveDataRef.current = {
            previousMoves: {},
            currentMoveIndex: 0
        };
    };

    const handleTrailerSelect = (trailer, moveIndex) => {
        console.log(`Selecting trailer ${trailer.trailer_number} for move ${moveIndex + 1}`);

        // Store the selected trailer for this move
        setMoveSelections(prev => ({
            ...prev,
            [moveIndex]: trailer
        }));

        // Update move details with this selection
        setMoveDetails(prev => {
            const updatedMoves = [...prev.moves];
            updatedMoves[moveIndex] = {
                ...updatedMoves[moveIndex],
                trailer: trailer
            };

            console.log('Updated moves after trailer selection:', updatedMoves);
            return {
                ...prev,
                moves: updatedMoves
            };
        });
    };

    // Get all possible locations for "to" dropdown
    const getAllLocations = () => {
        const locations = ['Yard', 'Shop']; // Start with standard locations

        // Add building locations with door numbers
        buildings
            .filter(building => building.account_id === selectedAccountId)
            .forEach(building => {
                // Add doors for each building
                for (let i = 1; i <= building.building_door_number; i++) {
                    locations.push(`${building.building_name}-${i}`);
                }
            });

        // Filter out "In Progress" from locations
        const filteredLocations = locations.filter(location => location !== "Road");

        // Custom sort function to handle numeric parts correctly
        return filteredLocations.sort((a, b) => {
            // If either location is a standard location (Yard or Shop), handle specially
            if (locations.includes(a) && !locations.includes(b)) return -1;
            if (!locations.includes(a) && locations.includes(b)) return 1;
            if (locations.includes(a) && locations.includes(b)) {
                return locations.indexOf(a) - locations.indexOf(b);
            }

            // Split locations into parts (e.g., "dc-1" becomes ["dc", "1"])
            const partsA = a.split('-');
            const partsB = b.split('-');

            // Compare building names first
            if (partsA[0] !== partsB[0]) {
                return partsA[0].localeCompare(partsB[0]);
            }

            // If building names are the same, compare door numbers numerically
            if (partsA.length > 1 && partsB.length > 1) {
                return parseInt(partsA[1]) - parseInt(partsB[1]);
            }

            return a.localeCompare(b);
        });
    };

    // Get only locations that have trailers for "from" dropdown
    const getFromLocations = () => {
        if (loadingTrailers) {
            console.log('Still loading trailer locations...');
            return ['Loading locations...'];
        }

        if (locationsWithTrailers.length === 0) {
            console.log('No locations with trailers found, showing standard locations as fallback');
            return ['Yard', 'Shop'];
        }

        console.log('Available trailer locations:', locationsWithTrailers);

        // Filter out "In Progress" from available locations
        const filteredLocations = locationsWithTrailers.filter(location => location !== "Road");

        // Sort locations with trailers using the same logic as getAllLocations
        return filteredLocations.sort((a, b) => {
            const standardLocations = ['Yard', 'Shop'];
            // If either location is a standard location (Yard or Shop), handle specially
            if (standardLocations.includes(a) && !standardLocations.includes(b)) return -1;
            if (!standardLocations.includes(a) && standardLocations.includes(b)) return 1;
            if (standardLocations.includes(a) && standardLocations.includes(b)) {
                return standardLocations.indexOf(a) - standardLocations.indexOf(b);
            }

            // Split locations into parts (e.g., "dc-1" becomes ["dc", "1"])
            const partsA = a.split('-');
            const partsB = b.split('-');

            // Compare building names first
            if (partsA[0] !== partsB[0]) {
                return partsA[0].localeCompare(partsB[0]);
            }

            // If building names are the same, compare door numbers numerically
            if (partsA.length > 1 && partsB.length > 1) {
                return parseInt(partsA[1]) - parseInt(partsB[1]);
            }

            return a.localeCompare(b);
        });
    };

    const handleLocationChange = (value, moveIndex, locationType) => {
        console.log(`Location change: ${locationType} for move ${moveIndex + 1} to ${value}`);

        // Track the current move being edited
        moveDataRef.current.currentMoveIndex = moveIndex;

        // Get the current state before update
        const currentMove = moveDetails.moves[moveIndex] || {};
        const isChangingSameValue = currentMove[locationType] === value;

        // If we're trying to set the same value, do nothing
        if (isChangingSameValue) {
            console.log(`Location ${locationType} already set to ${value}, skipping update`);
            return;
        }

        // Update move details
        setMoveDetails(prev => {
            const updatedMoves = [...prev.moves];

            if (locationType === 'from') {
                updatedMoves[moveIndex] = {
                    ...updatedMoves[moveIndex],
                    [locationType]: value,
                    trailer: null // Only reset trailer when changing "from" location
                };

                // Also clear the saved selection for this move
                setMoveSelections(prev => {
                    const updated = { ...prev };
                    delete updated[moveIndex];
                    return updated;
                });
            } else {
                updatedMoves[moveIndex] = {
                    ...updatedMoves[moveIndex],
                    [locationType]: value
                };
            }

            // Remove global error for same from/to location
            // Only clear error if both fields are filled and not the same
            const from = updatedMoves[moveIndex].from;
            const to = updatedMoves[moveIndex].to;
            if (from && to && from !== to) {
                setError(null);
            }

            return {
                ...prev,
                moves: updatedMoves
            };
        });

        // If this is a "from" location change, trigger trailer loading
        if (locationType === 'from' && value) {
            handleFromLocationSelect(value, moveIndex);
        }
    };

    const handleDoorSelection = (doorNumber, moveIndex, locationType) => {
        const building = selectedBuildings[moveIndex]?.[locationType];
        if (building) {
            setMoveDetails(prev => ({
                ...prev,
                moves: prev.moves.map((move, idx) =>
                    idx === moveIndex
                        ? { ...move, [locationType]: `${building.building_name}-${doorNumber}` }
                        : move
                )
            }));
        }
    };

    const handleFromLocationSelect = async (location, moveIndex) => {
        if (!location || !selectedAccountId) {
            console.log('Missing required data:', { location, selectedAccountId });
            return;
        }

        // Check if we're already loading trailers for this move index
        if (loadingMoveIndex === moveIndex) {
            console.log(`Already loading trailers for move ${moveIndex + 1}, skipping duplicate request`);
            return;
        }

        // Check if this location already has trailers loaded
        const existingTrailers = moveTrailers[moveIndex] || [];
        const currentMove = moveDetails.moves[moveIndex] || {};

        // If we already have trailers for this location and it matches the current move's from location,
        // don't fetch again
        if (existingTrailers.length > 0 && currentMove.from === location) {
            console.log(`Already loaded trailers for location ${location} for move ${moveIndex + 1}, skipping fetch`);
            return;
        }

        try {
            console.log(`Loading trailers for move ${moveIndex + 1}, location: ${location}`);

            // Mark which move is currently loading trailers
            setLoadingMoveIndex(moveIndex);

            // Use the getAvailableTrailers thunk instead of direct fetch
            const result = await dispatch(getAvailableTrailers({
                location,
                accountId: selectedAccountId,
                excludeRedTagged: false
            }));

            // Check if we got results back
            if (result && Object.keys(result).length > 0) {
                const trailerArray = Object.values(result);
                console.log(`Got ${trailerArray.length} trailers for move ${moveIndex + 1}:`, trailerArray);

                // We also need to fetch requested moves to filter out trailers already in requested moves
                try {
                    const movesResponse = await fetch(`/api/moves?account_id=${selectedAccountId}`, {
                        headers: {
                            'Content-Type': 'application/json',
                            'X-CSRF-TOKEN': getCookie('csrf_token')
                        },
                        credentials: 'include'
                    });

                    if (movesResponse.ok) {
                        const allMoves = await movesResponse.json();

                        // Filter out trailers that are in requested or active moves
                        const requestedMoves = allMoves.filter(move =>
                            !move.in_progress && !move.finish_time && !move.is_cancel
                        );

                        const trailersInRequestedMoves = new Set();
                        requestedMoves.forEach(move => {
                            if (move.move_details && move.move_details.moves) {
                                move.move_details.moves.forEach(moveStep => {
                                    if (moveStep.trailer) {
                                        trailersInRequestedMoves.add(moveStep.trailer);
                                    }
                                });
                            }
                        });

                        // Filter out trailers that are in requested moves
                        const availableTrailers = trailerArray.filter(trailer =>
                            !trailersInRequestedMoves.has(trailer.trailer_number)
                        );

                        console.log(`Filtered out ${trailerArray.length - availableTrailers.length} trailers that are in requested moves`);

                        // Store trailers for this specific move
                        setMoveTrailers(prev => ({
                            ...prev,
                            [moveIndex]: availableTrailers
                        }));
                    } else {
                        // If fetching moves fails, just use the original trailer array
                        setMoveTrailers(prev => ({
                            ...prev,
                            [moveIndex]: trailerArray
                        }));
                    }
                } catch (error) {
                    console.error('Error fetching moves:', error);
                    // If there's an error, just use the original trailer array
                    setMoveTrailers(prev => ({
                        ...prev,
                        [moveIndex]: trailerArray
                    }));
                }
            } else {
                console.log(`No trailers found for move ${moveIndex + 1} at location ${location}`);
                setMoveTrailers(prev => ({
                    ...prev,
                    [moveIndex]: []
                }));
            }
        } catch (err) {
            console.error(`Error loading trailers for move ${moveIndex + 1}:`, err);
            setError('Failed to load available trailers');
        } finally {
            setLoadingMoveIndex(null);
            // Clear trailer search
            setTrailerSearch('');
        }
    };

    const renderTrailerOptions = (moveIndex) => {
        const move = moveDetails.moves[moveIndex];

        if (!move.from) {
            return <p className="text-gray-500 italic">Please select a 'from' location first</p>;
        }

        // Show loading indicator while fetching trailers
        if (loadingMoveIndex === moveIndex) {
            return (
                <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                    <div className="ml-4 text-2xl text-blue-600">Loading...</div>
                </div>
            );
        }

        // Get trailers specific to this move
        const trailersForThisMove = moveTrailers[moveIndex] || [];

        console.log(`Rendering ${trailersForThisMove.length} trailers for move ${moveIndex + 1}`);
        console.log(`Selected trailer for move ${moveIndex + 1}:`, move.trailer);

        // Always show the search bar regardless of whether there are trailers available
        const trailerSearchId = `trailer-search-${moveIndex}`;
        const searchBar = (
            <div className="relative">
                <label htmlFor={trailerSearchId} className="sr-only">Search trailers for move {moveIndex + 1}</label>
                <input
                    id={trailerSearchId}
                    name={trailerSearchId}
                    type="text"
                    placeholder="Search trailers..."
                    value={trailerSearch}
                    onChange={(e) => setTrailerSearch(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </div>
                {trailerSearch && (
                    <div
                        className="absolute inset-y-0 right-8 flex items-center cursor-pointer"
                        onClick={() => setTrailerSearch('')}
                    >
                        <svg className="h-4 w-4 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </div>
                )}
            </div>
        );

        // If there are no trailers at all for this location
        if (trailersForThisMove.length === 0) {
            return (
                <div className="space-y-4">
                    {searchBar}
                    <p className="text-gray-500 italic">No trailers available at this location</p>
                    <p className="text-gray-500 italic">Note: Trailers that are in requested or active moves are not shown</p>
                </div>
            );
        }

        // Get trailers already selected in other moves in this session
        const selectedTrailerIds = new Set();
        moveDetails.moves.forEach((otherMove, idx) => {
            if (idx !== moveIndex && otherMove.trailer) {
                selectedTrailerIds.add(otherMove.trailer.id);
            }
        });

        console.log(`Trailers already selected in other moves: ${Array.from(selectedTrailerIds).join(', ')}`);

        // Filter trailers based on search
        // Also exclude trailers already selected in other moves
        // And exclude trailers with location "In Progress"
        const filteredTrailers = trailersForThisMove
            .filter(trailer => !selectedTrailerIds.has(trailer.id)) // Filter out trailers selected in other moves
            .filter(trailer => trailer.trailer_location !== "Road") // Filter out in-progress trailers
            .filter(trailer =>
                trailer.trailer_number.toLowerCase().includes(trailerSearch.toLowerCase()) ||
                (trailer.type && trailer.type.toLowerCase().includes(trailerSearch.toLowerCase()))
            );

        if (filteredTrailers.length === 0) {
            // Check if there are any trailers that would match without the "already selected" filter
            const onlySearchFiltered = trailersForThisMove
                .filter(trailer =>
                    trailer.trailer_number.toLowerCase().includes(trailerSearch.toLowerCase()) ||
                    (trailer.type && trailer.type.toLowerCase().includes(trailerSearch.toLowerCase()))
                );

            if (onlySearchFiltered.length > 0 && onlySearchFiltered.every(t => selectedTrailerIds.has(t.id))) {
                return (
                    <div className="space-y-4">
                        {searchBar}
                        <p className="text-gray-500 italic">All matching trailers are already selected in other moves</p>
                    </div>
                );
            }

            return (
                <div className="space-y-4">
                    {searchBar}
                    <p className="text-gray-500 italic">No available trailers match your search</p>
                </div>
            );
        }

        return (
            <div className="space-y-4">
                {/* Search bar */}
                {searchBar}

                {/* Trailer selection status */}
                {move.trailer ? (
                    <div className="p-2 bg-green-50 border border-green-200 rounded-md">
                        <div className="flex items-center">
                            <div className="flex items-center justify-center w-6 h-6 bg-green-500 rounded-full mr-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <p className="text-green-700">
                                Selected Trailer: <span className="font-bold">{move.trailer.trailer_number}</span>
                            </p>
                        </div>
                    </div>
                ) : (
                    <div className="p-2 bg-red-50 border border-red-200 rounded-md">
                        <div className="flex items-center">
                            <div className="flex items-center justify-center w-6 h-6 bg-red-500 rounded-full mr-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                            <p className="text-red-700">
                                Please select a trailer from the list below
                            </p>
                        </div>
                    </div>
                )}

                {/* Scrollable trailer list */}
                <div className="overflow-y-auto max-h-60 rounded-md border border-gray-200">
                    <div className="grid grid-cols-1 gap-2 p-2">
                        {filteredTrailers.map(trailer => (
                            <div
                                key={trailer.id}
                                className={`p-3 border rounded-lg cursor-pointer ${move.trailer?.id === trailer.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                                    }`}
                                onClick={() => handleTrailerSelect(trailer, moveIndex)}
                            >
                                <div className="flex justify-between items-center">
                                    <div>
                                        <p className="font-medium text-xl">{trailer.trailer_number}</p>
                                        <p className="text-sm text-gray-500">{trailer.type}</p>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        {trailer.is_loaded && (
                                            <span className="px-2 py-1 text-sm bg-blue-100 text-blue-800 rounded">Loaded</span>
                                        )}
                                        {trailer.yellow_tagged && (
                                            <span className="px-2 py-1 text-sm bg-yellow-100 text-yellow-800 rounded">Yellow Tagged</span>
                                        )}
                                        {trailer.red_tagged && (
                                            <span className="px-2 py-1 text-sm bg-red-100 text-red-800 rounded">Red Tagged</span>
                                        )}
                                        {trailer.in_shop && (
                                            <span className="px-2 py-1 text-sm bg-gray-100 text-gray-800 rounded">Shop</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    // Check if form is valid (all required fields are filled)
    const isFormValid = () => {
        // Check if all moves have both locations and a trailer selected
        return moveDetails.moves.every(move =>
            move.from &&
            move.to &&
            move.trailer &&
            move.from !== move.to
        );
    };

    // Check if locations are selected for a specific move
    const areLocationsSelected = (moveIndex) => {
        const move = moveDetails.moves[moveIndex];
        return move && move.from && move.to && move.from !== move.to;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        // Validate all moves have trailers selected
        const missingTrailers = moveDetails.moves.some(move => !move.trailer);
        if (missingTrailers) {
            setError('Please select a trailer for each move');
            return;
        }

        // Validate locations are selected
        const missingLocations = moveDetails.moves.some(move => !move.from || !move.to);
        if (missingLocations) {
            setError('Please select both from and to locations for each move');
            return;
        }

        // Remove global error for same from/to location
        const sameLocations = moveDetails.moves.some(move => move.from === move.to);
        if (sameLocations) {
            // Do not set global error, just prevent submit
            return;
        }

        // Check if any trailer is on the road or in progress
        const onRoadTrailers = moveDetails.moves
            .filter(move => move.trailer)
            .filter(move => move.trailer.trailer_location === "Road");

        if (onRoadTrailers.length > 0) {
            const trailerNumbers = onRoadTrailers.map(move => move.trailer.trailer_number).join(", ");
            setError(`Cannot create move with trailers that are already on the road: ${trailerNumbers}`);
            return;
        }

        const moveData = {
            account_id: selectedAccountId,
            request_user_id: currentUser.id,
            move_details: {
                type: moveType,
                moves: moveDetails.moves.map(move => ({
                    from: move.from,
                    to: move.to,
                    trailer: move.trailer.trailer_number
                }))
            },
            in_progress: false,
            is_cancel: false,
        };

        try {
            setIsSubmitting(true);
            const result = await dispatch(createMove(moveData));

            console.log('Move created successfully:', result);

            // Reset form
            setMoveType('single');
            setMoveDetails({
                type: 'single',
                moves: [{ from: '', to: '', trailer: null }]
            });
            setTrailerSearch('');
            setMoveSelections({});
            setIsSubmitting(false);

            onClose();
        } catch (err) {
            console.error('Move creation error:', err);
            setError(err.message || 'Failed to create move. Please try again.');
            setIsSubmitting(false);
        }
    };

    const LocationSelector = ({ moveIndex, locationType, value, onChange }) => {
        // Standard locations that should always be available in both dropdowns
        const standardLocations = ['Yard', 'Shop'];

        // State for search, dropdown visibility, and keyboard navigation
        const [isOpen, setIsOpen] = useState(false);
        const [searchTerm, setSearchTerm] = useState('');
        const [activeIndex, setActiveIndex] = useState(-1);
        const dropdownRef = useRef(null);

        // Close dropdown when clicking outside
        useEffect(() => {
            const handleClickOutside = (event) => {
                if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                    setIsOpen(false);
                    setSearchTerm(''); // Clear search term when closing dropdown
                }
            };

            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, []);

        // Reset active index when filtered locations change
        useEffect(() => {
            setActiveIndex(-1);
        }, [searchTerm]);

        // Get appropriate locations based on dropdown type
        let locations = [];

        if (locationType === 'from') {
            // "From" dropdown shows only locations with trailers
            locations = getFromLocations();

            // For move 2 and 3 in shuttle/triangle types, exclude previously selected from locations
            if (moveIndex > 0) {
                // Get all previously selected "from" locations from earlier moves
                const previousFromLocations = moveDetails.moves
                    .slice(0, moveIndex) // Get only previous moves
                    .map(move => move.from) // Extract "from" locations
                    .filter(Boolean); // Remove empty values

                console.log(`Move ${moveIndex + 1} previous from locations:`, previousFromLocations);

                // Filter out previously selected locations unless they're standard locations
                locations = locations.filter(location =>
                    !previousFromLocations.includes(location) || standardLocations.includes(location)
                );
            }
        } else {
            // For "To" location dropdown:

            // Start with all possible locations
            let availableToLocations = getAllLocations();

            // Get locations that have trailers (to potentially exclude)
            const locationsWithTrailers = getFromLocations();

            // Get previous moves' "from" locations (these should be available as destinations)
            const previousFromLocations = moveIndex > 0
                ? moveDetails.moves
                    .slice(0, moveIndex)
                    .map(move => move.from)
                    .filter(Boolean)
                : [];

            // Get previous moves' "to" locations (these should be excluded)
            const previousToLocations = moveIndex > 0
                ? moveDetails.moves
                    .slice(0, moveIndex)
                    .map(move => move.to)
                    .filter(Boolean)
                : [];

            console.log(`Move ${moveIndex + 1} To dropdown - previous from:`, previousFromLocations, 'previous to:', previousToLocations);

            // Filter for "To" locations:
            // 1. Include locations without trailers
            // 2. Include previous moves' "from" locations (they're now empty)
            // 3. Include standard locations (Yard, Shop)
            // 4. Exclude previous moves' "to" locations (they're now occupied)
            locations = availableToLocations.filter(location => {
                // Keep if it's a standard location
                if (standardLocations.includes(location)) {
                    return true;
                }

                // Keep if it's a from location from a previous move (now empty)
                if (previousFromLocations.includes(location)) {
                    return true;
                }

                // Exclude if it's a to location from a previous move (now occupied)
                if (previousToLocations.includes(location)) {
                    return false;
                }

                // Otherwise keep only if it doesn't have trailers
                return !locationsWithTrailers.includes(location);
            });
        }

        // Apply search filter if there's a search term
        const filteredLocations = searchTerm
            ? locations.filter(location =>
                location.toLowerCase().includes(searchTerm.toLowerCase()))
            : locations;

        // Determine if this move has a from/to conflict
        const move = moveDetails.moves[moveIndex];
        const from = move?.from;
        const to = move?.to;
        const showSameLocationError = from && to && from === to;

        // Handle selection
        const handleSelect = (location) => {
            onChange(location);
            setIsOpen(false);
            setSearchTerm('');
        };

        // Handle keyboard navigation
        const handleKeyDown = (e) => {
            if (!isOpen) return;

            // Arrow down
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActiveIndex(prevIndex =>
                    prevIndex < filteredLocations.length - 1 ? prevIndex + 1 : prevIndex
                );
            }

            // Arrow up
            else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveIndex(prevIndex => prevIndex > 0 ? prevIndex - 1 : 0);
            }

            // Enter key
            else if (e.key === 'Enter' && activeIndex >= 0) {
                e.preventDefault();
                handleSelect(filteredLocations[activeIndex]);
            }

            // Escape key
            else if (e.key === 'Escape') {
                setIsOpen(false);
                setActiveIndex(-1);
                setSearchTerm('');
            }
        };

        // Handle input focus
        const handleInputFocus = () => {
            setIsOpen(true);
            // Don't override the current value with a search term when focusing
            setSearchTerm('');
        };

        // Handle input change
        const handleInputChange = (e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);

            // If search field is cleared, also clear the selected value
            if (e.target.value === '') {
                onChange('');
            }
        };

        // Generate unique ID for this location selector
        const locationInputId = `location-${locationType}-${moveIndex}`;

        return (
            <div className="space-y-2 relative" ref={dropdownRef}>
                <div className="relative">
                    <div
                        className={`relative w-full p-3 border rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none ${
                            showSameLocationError
                                ? 'border-red-500'
                                : value
                                    ? 'border-green-500'
                                    : 'border-gray-300'
                        }`}
                    >
                        <input
                            id={locationInputId}
                            name={locationInputId}
                            type="text"
                            className="w-full border-none focus:ring-0 focus:outline-none text-xl bg-transparent"
                            placeholder="Select Location"
                            // Display the selected value, or the search term when searching
                            value={isOpen && searchTerm !== '' ? searchTerm : value || ''}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            onKeyDown={handleKeyDown}
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                            <svg className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                            </svg>
                        </div>

                        {/* Icon logic: show red X if error, green check if valid */}
                        {showSameLocationError && (
                            <div className="absolute inset-y-0 right-10 flex items-center">
                                <div className="flex items-center justify-center w-6 h-6 bg-red-500 rounded-full">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </div>
                            </div>
                        )}
                        {!showSameLocationError && value && (
                            <div className="absolute inset-y-0 right-10 flex items-center">
                                <div className="flex items-center justify-center w-6 h-6 bg-green-500 rounded-full">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                            </div>
                        )}

                        {/* Clear button when value is selected */}
                        {value && !isOpen && (
                            <button
                                type="button"
                                className="absolute inset-y-0 right-16 flex items-center text-gray-400 hover:text-gray-600"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onChange('');
                                }}
                            >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>

                    {isOpen && (
                        <ul className="absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto z-50 bg-white shadow-lg rounded-md border border-gray-300">
                            {filteredLocations.length === 0 ? (
                                <li className="p-3 text-gray-500 text-center">
                                    No results found
                                </li>
                            ) : (
                                filteredLocations.map((location, index) => (
                                    <li
                                        key={`${moveIndex}-${locationType}-${location}-${index}`}
                                        onClick={() => handleSelect(location)}
                                        className={`p-3 cursor-pointer text-xl border-b border-gray-200 ${
                                            index === activeIndex ? 'bg-blue-100' : 'hover:bg-blue-50'
                                        } ${value === location ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                                    >
                                        <span>{location}</span>
                                    </li>
                                ))
                            )}
                        </ul>
                    )}
                </div>

                {!value && !showSameLocationError && (
                    <div className="text-red-500 text-sm mt-1">
                        Please select a {locationType} location
                    </div>
                )}
                {showSameLocationError && (
                    <div className="text-red-500 text-sm mt-1">
                        Cannot select the same FROM / TO Location
                    </div>
                )}
            </div>
        );
    };

    // Don't render anything if not open or no permission
    if (!isOpen || !hasPermission) {
        return null;
    }

    return (
        <>
            {/* Modal Backdrop */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
                onClick={onClose}
                style={{ zIndex: 9998 }}
            />

            {/* Modal Panel */}
            <div className="fixed inset-0 z-10 overflow-y-auto" style={{ zIndex: 9999 }}>
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <div
                        className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-5xl"
                        style={{
                            minHeight: moveType === 'single' ? '560px' : 'auto',
                            maxHeight: '80vh',
                            transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                            zIndex: 10000
                        }}
                    >
                        <div
                            className="bg-white px-6 pb-6 pt-6 sm:p-8 overflow-y-auto"
                            style={{
                                maxHeight: 'calc(80vh - 40px)',
                                transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        >
                            <div className="flex justify-between items-center mb-8">
                                <h2 className="text-3xl font-semibold text-gray-900">Create Move</h2>
                                <button
                                    onClick={() => {
                                        setTrailerSearch('');
                                        setMoveSelections({});
                                        onClose();
                                    }}
                                    className="text-gray-400 hover:text-gray-500"
                                >
                                    <span className="sr-only">Close</span>
                                    <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* Modal Content */}
                            {isLoading ? (
                                <div className="flex justify-center items-center h-64">
                                    <div className="flex items-center justify-center">
                                        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
                                        <div className="ml-4 text-2xl text-blue-600">Loading...</div>
                                    </div>
                                </div>
                            ) : error ? (
                                <div className="flex justify-center items-center h-64">
                                    <div className="text-2xl text-red-600">{error}</div>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-8">
                                    {/* Move Type Selection */}
                                    <div>
                                        <div className="block text-lg font-medium text-gray-700 mb-3">Move Type</div>
                                        <div className="flex space-x-4">
                                            {['single', 'shuttle', 'triangle'].map((type) => (
                                                <button
                                                    key={type}
                                                    type="button"
                                                    onClick={() => handleMoveTypeChange(type)}
                                                    className={`px-6 py-3 text-lg rounded-md ${moveType === type
                                                        ? 'bg-blue-600 text-white'
                                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                        }`}
                                                >
                                                    {type.charAt(0).toUpperCase() + type.slice(1)}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Move Locations */}
                                    <div className="space-y-8">
                                        {moveDetails.moves.map((move, index) => (
                                            <div key={index} className="space-y-6 p-6 bg-gray-50 rounded-lg">
                                                <div>
                                                    <div className="block text-lg font-medium text-gray-700 mb-3">
                                                        Move {index + 1}
                                                    </div>
                                                    <div className="space-y-6">
                                                        <div>
                                                            <label htmlFor={`location-from-${index}`} className="block text-lg font-medium text-gray-700 mb-3">
                                                                From Location
                                                            </label>
                                                            <LocationSelector
                                                                moveIndex={index}
                                                                locationType="from"
                                                                value={move.from}
                                                                onChange={(value) => handleLocationChange(value, index, 'from')}
                                                            />
                                                            {loadingTrailers && (
                                                                <p className="mt-2 text-sm text-gray-500">
                                                                    Loading...
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <label htmlFor={`location-to-${index}`} className="block text-lg font-medium text-gray-700 mb-3">
                                                                To Location
                                                            </label>
                                                            <LocationSelector
                                                                moveIndex={index}
                                                                locationType="to"
                                                                value={move.to}
                                                                onChange={(value) => handleLocationChange(value, index, 'to')}
                                                            />
                                                        </div>
                                                        {/* Trailer Selection for this move */}
                                                        <div>
                                                            <div className="block text-lg font-medium text-gray-700 mb-3">
                                                                Available Trailers for Move {index + 1}
                                                            </div>
                                                            {renderTrailerOptions(index)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="bg-gray-50 px-6 py-4 sm:px-8">
                                        <div className="sm:flex sm:flex-row-reverse">
                                            <button
                                                type="submit"
                                                className={`inline-flex w-full justify-center rounded-md ${isFormValid()
                                                    ? 'bg-blue-600 hover:bg-blue-500'
                                                    : 'bg-blue-300 cursor-not-allowed'
                                                    } px-6 py-3 text-lg font-semibold text-white shadow-sm sm:ml-4 sm:w-auto`}
                                                disabled={!isFormValid() || isSubmitting}
                                            >
                                                {isSubmitting ? (
                                                    <span className="flex items-center">
                                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        Creating...
                                                    </span>
                                                ) : (
                                                    "Create Move"
                                                )}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={onClose}
                                                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-6 py-3 text-lg font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                                disabled={isSubmitting}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default MoveCreationModal;
